import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../utils/supabaseClient';

interface Tenant {
    id: string;
    nome_salao: string;
    // Standard fields
    name?: string;
    owner_id?: string;
    plan_id?: string;
    address_street?: string;
    address_city?: string;
    address_state?: string;
    phone?: string;
    segment?: string;
    subscription_status?: string;
    trial_ends_at?: string;
    // Theme fields
    theme_mode?: string;
    accent_color?: string;
    [key: string]: any; // Allow other fields
}

interface TenantContextType {
    tenant: Tenant | null;
    tenantId: string | null;
    tenantName: string | null;
    loading: boolean;
    error: string | null;
    needsTenantSelection: boolean;
    selectTenant: (id: string, name: string) => void;
    updateTenant: (updates: Partial<Tenant>) => Promise<{ success: boolean; error?: any }>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { profile, user, loading: authLoading } = useAuth();
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [tenantId, setTenantId] = useState<string | null>(null);
    const [tenantName, setTenantName] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [needsTenantSelection, setNeedsTenantSelection] = useState(false);

    // Function to update tenant details
    const updateTenant = async (updates: Partial<Tenant>) => {
        if (!tenantId) return { success: false, error: 'No tenant selected' };

        try {
            // Determine which table to update based on where we loaded from
            // Ideally we check both or prioritize tents
            // For now, we try 'tenants' then 'saloes' if 'tenants' fails or returns empty?
            // Actually, we should know which table. But the context logic tries 'tenants' then 'saloes'.
            // Let's try updating 'saloes' first as it is the legacy/core table for now?
            // Or better, update based on what we have.
            // Let's assume 'saloes' for this migration scope.

            const { error: errorSaloes } = await supabase
                .from('saloes')
                .update(updates)
                .eq('id', tenantId);

            if (errorSaloes) {
                // If failed (maybe it's in tenants table only?), try tenants
                const { error: errorTenants } = await supabase
                    .from('tenants')
                    .update(updates)
                    .eq('id', tenantId);

                if (errorTenants) throw errorTenants;
            } else {
                // Try to update tenants too just in case it exists there (sync)
                await supabase.from('tenants').update(updates).eq('id', tenantId);
            }

            // Update local state
            setTenant(prev => prev ? { ...prev, ...updates } : null);
            return { success: true };
        } catch (err: any) {
            console.error('Error updating tenant:', err);
            return { success: false, error: err.message };
        }
    };

    // Function to manually select a tenant
    const selectTenant = async (id: string, name: string) => {
        // Log removed
        setTenantId(id);
        setTenantName(name);
        setNeedsTenantSelection(false);
        // Mark as resolved so useEffect doesn't overwrite with stale profile data
        if (user) {
            resolvedSessionId.current = user.id;
        }
        setError(null);

        // Fetch full tenant data from database
        try {
            // Try tenants table first
            const { data: tenantData } = await supabase
                .from('tenants')
                .select('*')
                .eq('id', id)
                .single();

            if (tenantData) {
                // Log removed
                setTenant({ ...tenantData, nome_salao: tenantData.name });
                return;
            }

            // Fallback to saloes
            const { data: saloesData } = await supabase
                .from('saloes')
                .select('*')
                .eq('id', id)
                .single();

            if (saloesData) {
                // Log removed
                setTenant({ ...saloesData, name: saloesData.nome_salao });
                return;
            }

            // If nothing found, use basic props
            setTenant({ id, nome_salao: name, name });
        } catch (e) {
            console.warn('[TenantContext] Error fetching tenant details:', e);
            setTenant({ id, nome_salao: name, name });
        }
    };

    // Track if we've already tried to resolve the tenant for this session
    const resolvedSessionId = React.useRef<string | null>(null);

    useEffect(() => {
        // Guard against infinite loading
        // Guard against infinite loading
        const timeout = setTimeout(() => {
            if (loading && !resolvedSessionId.current) {
                console.warn('[TenantContext] Timeout reached');
                setLoading(false);
            }
        }, 15000);

        const resolveTenant = async () => {
            // Wait for auth
            if (authLoading) return;

            // Handle unauthenticated state
            if (!user) {
                if (tenantId) {
                    setTenantId(null);
                    setTenantName(null);
                    setTenant(null);
                }
                setNeedsTenantSelection(false);
                setLoading(false);
                resolvedSessionId.current = null;
                return;
            }

            // Optimization: If we already have a tenant matching the profile, skip
            if (tenantId === profile?.tenant_id && tenantId !== null) {
                if (loading) setLoading(false);
                return;
            }

            // Optimization: If we already resolved for this user and profile is still the same (null), keep current state
            if (resolvedSessionId.current === user.id && !profile?.tenant_id && needsTenantSelection) {
                if (loading) setLoading(false);
                return;
            }

            // Log removed
            resolvedSessionId.current = user.id;

            // 1. Check Profile
            if (profile?.tenant_id) {
                // Log removed
                setTenantId(profile.tenant_id);

                // Fetch details in background if needed, but set ID immediately to unblock UI
                // Only fetch if we don't have the object or it mismatches
                if (tenant?.id !== profile.tenant_id) {
                    try {
                        const { data } = await supabase.from('tenants').select('*').eq('id', profile.tenant_id).single();
                        if (data) {
                            if (tenant?.id !== data.id) {
                                setTenant({ ...data, nome_salao: data.name });
                                setTenantName(data.name);
                            }
                        } else {
                            // Try legacy
                            const { data: legacy } = await supabase.from('saloes').select('*').eq('id', profile.tenant_id).single();
                            if (legacy) {
                                setTenant(legacy);
                                setTenantName(legacy.nome_salao);
                            }
                        }
                    } catch (e) { console.warn('Error fetching tenant details', e); }
                }

                setError(null);
                setNeedsTenantSelection(false);
                setLoading(false);
                return;
            }

            // 2. If no profile tenant_id, check ownership
            // ... (rest of logic)
            // Ideally, we should redirect to selection if profile doesn't have it, 
            // BUT providing a fast path for single-tenant owners is nice.
            // However, doing it here AND in TenantSelectionPage causes the race.
            // LET'S DISABLE AUTO-SELECTION HERE if it causes conflicts.
            // Actually, let's keep it but make sure we don't loop.

            // If we get here, it means profile.tenant_id is null.
            // We set needsTenantSelection = true and let the Page handle the logic.
            // The Page will auto-select if there is only one.
            // This prevents "Context tries to select" vs "Page tries to select" race.

            // Log removed
            setNeedsTenantSelection(true);
            setLoading(false);
        };

        resolveTenant();

        return () => clearTimeout(timeout);
    }, [user?.id, profile?.tenant_id, authLoading]);

    return (
        <TenantContext.Provider value={{
            tenant,
            tenantId,
            tenantName,
            loading,
            error,
            needsTenantSelection,
            selectTenant,
            updateTenant
        }}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error('useTenant deve ser usado dentro de um TenantProvider');
    }
    return context;
};
